import { useSdkKeys } from "@/features/sdk-keys/api";
import { useKeyConfig } from "@/features/simulator/api";
import { useTimezone } from "@/features/timezone/api";
import { useWebhook } from "@/features/webhooks/api";
import { useApp } from "@rook/shared/lib/appContext";

/**
 * Aggregated "is the customer set up?" signal per nav item, read
 * by the sidebar to render amber attention dots on the rows that
 * still need configuration. Mirrors the legacy `getNavStatus()`
 * prefetch (`refactor-next-stable:src/modules/home/server/`) but
 * client-side, piggy-backing on the per-feature queries already
 * in flight when the customer is using those views.
 *
 * Returns `attentionByPath` — a sparse map of `"/home/X" → true`
 * for routes that need attention. Routes not in the map are
 * considered configured (or "we don't track this one").
 *
 * Each upstream query is cached + de-duped by TanStack Query, so
 * mounting the sidebar doesn't re-fetch when the active view is
 * already showing that data.
 */
export type NavStatus = {
  attentionByPath: Record<string, boolean>;
  /** True while any of the underlying queries is still loading. */
  loading: boolean;
};

export const useNavStatus = (): NavStatus => {
  const { env } = useApp();
  const webhook = useWebhook(env);
  const timezone = useTimezone(env);
  const sdk = useSdkKeys(env);
  const keyConfig = useKeyConfig(env);

  const attentionByPath: Record<string, boolean> = {};

  // Webhook: needs an endpoint set, otherwise nothing is delivered.
  if (webhook.data && (webhook.data.endpoint ?? "") === "") {
    attentionByPath["/home/delivery/webhooks"] = true;
  }

  // Timezone: amber when the customer hasn't moved off the UTC
  // default. Not strictly broken — UTC delivers — but most
  // customers want their local time as the fallback.
  if (timezone.data && (timezone.data.time_zone ?? "UTC") === "UTC") {
    attentionByPath["/home/extraction/timezone"] = true;
  }

  // SDK keys: amber when neither identifier is registered. Only
  // matters for mobile-SDK customers; API-only customers will
  // ignore the dot — that's fine.
  if (
    sdk.data &&
    (sdk.data.bundle_id ?? "") === "" &&
    (sdk.data.package_name ?? "") === ""
  ) {
    attentionByPath["/home/auth/sdk"] = true;
  }

  // Data structure: amber when the customer hasn't saved a
  // configuration yet (404 from /customize/key_configuration →
  // useKeyConfig resolves to null). Once saved at least once,
  // the dot clears.
  if (keyConfig.data === null) {
    attentionByPath["/home/delivery/data-structure"] = true;
  }

  return {
    attentionByPath,
    loading:
      webhook.isLoading ||
      timezone.isLoading ||
      sdk.isLoading ||
      keyConfig.isLoading,
  };
};
